import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Nav,
  NavItem,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  Label,
  Button,
  NavLink,
  Badge,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";

import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import JobApplicationService from "../../services/KangarooServices/JobApplicationService";
import {
  AppId,
  Name,
  Designation,
  Contact,
  Status,
  Type,
} from "../StudentForms/ApplicationCol";
import DeleteModal from "../../Components/Common/DeleteModal";
import TableContainer from "../../Components/Common/TableContainer";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";

const JobApplication = () => {
  const [application, setApplication] = useState({});
  const [appList, setAppList] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isStatusOpen, setIsStatusOpen] = useState(false);

  const toggleStatus = () => setIsStatusOpen(!isStatusOpen);

  const fetchList = async () => {
    try {
      const response = await JobApplicationService.getList();
      setAppList(response?.items || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleApplicationClick = useCallback((row) => {
    setApplication({
      _id: row.id,
      company: row.company,
      Designation: row.Designation,
      date: row.date,
      contact: row.contact,
      type: row.type,
      status: row.status,
    });
  }, []);

  const handleStatusUpdate = (item) => {
    setSelectedItem(item);
    toggleStatus();
  };

  const statusOptions = [
    { label: "New", value: 1 },
    { label: "In Review", value: 2 },
    { label: "Rejected", value: 3 },
    { label: "Accepted", value: 4 },
  ];

  const [selectedStatus, setSelectedStatus] = useState(null);

  const handleStatusChange = (e) => {
    setSelectedStatus(Number(e.target.value));
  };

  const handleUpdateStatus = async () => {
    if (selectedItem && selectedStatus !== null) {
      try {
        await JobApplicationService.updateStatus(selectedItem.id, {
          status: selectedStatus, // send numeric enum value
        });
        fetchList();
        toast.success("Status updated successfully");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to update status",
        );
      } finally {
        toggleStatus();
        setSelectedItem(null);
        setSelectedStatus(null);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "SN",
        selector: (row, index) => index + 1 || "N/A",
        sortable: true,
        width: "75px",
      },
      {
        name: "Student name",
        selector: (row) => row.fullName || "N/A",
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 ">{row.fullName || "N/A"}</div>
          </div>
        ),
      },
      {
        name: "Email",
        selector: (row) => row.email || "N/A",
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 ">{row.email || "N/A"}</div>
          </div>
        ),
      },
      {
        name: "Phone",
        selector: (row) => row.phoneNumber || "N/A",
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 ">{row.phoneNumber || "N/A"}</div>
          </div>
        ),
      },
      {
        name: "Position",
        selector: (row) =>
          row.jobListingPosition
            ? row.jobListingPosition
            : row.interestedPosition || "N/A",
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 ">
              {row.jobListingPosition
                ? row.jobListingPosition
                : row.interestedPosition || "N/A"}
            </div>
          </div>
        ),
      },
      {
        name: "Status",
        selector: (row) => row.jobApplicationStatus,
        sortable: true,
        cell: (row) => {
          let color;
          switch (row.jobApplicationStatus) {
            case "Accepted":
              color = "success";
              break;
            case "Rejected":
              color = "danger";
              break;
            case "InReview":
              color = "warning";
              break;
            default:
              color = "primary";
          }
          return <Badge color={color}>{row.jobApplicationStatus}</Badge>;
        },
      },

      {
        name: "Action",
        sortable: false,
        cell: (row) => (
          <div className="d-flex gap-3">
            <Link
              to={`/job-application/detail/${row.id}`}
              className="text-primary"
            >
              <Button
                size="sm"
                color="info"
                className="d-flex align-items-center"
              >
                <i className="ri-eye-fill " title="View"></i>
              </Button>
            </Link>

            <Button
              size="sm"
              color="warning"
              className="d-flex align-items-center"
              onClick={() => handleStatusUpdate(row)}
            >
              <i className="mdi mdi-pencil " title="Edit"></i>
            </Button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [],
  );

  // Optional: Custom styles to match your existing look
  const customStyles = {
    headCells: {
      style: {
        fontWeight: "bold",
        color: "#6c757d", // text-muted
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
      },
    },
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Application" pageTitle="User Management" />
          <Row>
            <Col>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-md-flex align-items-center">
                    <h5 className="card-title mb-3 mb-md-0 flex-grow-1">
                      Job Application
                    </h5>
                    {/* <div className="flex-shrink-0">
                      <div className="d-flex gap-1 flex-wrap">
                        <Button
                          type="button"
                          className="btn btn-primary add-btn"
                          data-bs-toggle="modal"
                          id="create-btn"
                          data-bs-target="#showModal"
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Create Application
                        </Button>
                      </div>
                    </div> */}
                  </div>
                </CardHeader>

                <CardBody className="pt-0">
                  <div className="w-100">
                    <DataTable
                      columns={columns}
                      data={appList}
                      pagination
                      paginationPerPage={8}
                      highlightOnHover
                      striped
                      customStyles={customStyles}
                      onRowClicked={handleApplicationClick} // Optional: if you want row click to select application
                      // clearSelectedRows prop can be used if you add selectableRows
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        <Modal isOpen={isStatusOpen} toggle={toggleStatus}>
          <ModalHeader toggle={toggleStatus}>Update Status</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label htmlFor="statusSelect" className="form-label">
                Select Status
              </label>
              <select
                id="statusSelect"
                className="form-select"
                value={
                  selectedStatus || selectedItem?.jobApplicationStatus || ""
                }
                onChange={handleStatusChange}
              >
                <option value="">-- Select Status --</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </ModalBody>

          <ModalFooter>
            <button className="btn btn-success" onClick={handleUpdateStatus}>
              Update
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={toggleStatus}
            >
              Cancel
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default JobApplication;
